import React, { FC, useEffect, useRef, useState } from 'react';
import { DesktopTimePicker, DesktopTimePickerProps, LocalizationProvider } from '@mui/x-date-pickers';
import { validateInput } from '../../utils/validators/Validators';

import './styles.css';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { FormValidators } from '../Form';
import { Box, TextField, useTheme } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/de';
import strings from 'common/Translation/Translate';
import 'dayjs/locale/pt';
import 'dayjs/locale/en';
import { useSelector } from 'react-redux';
import { defaultLanguage, parentOrgInitData } from 'utils/redux/reducer/authentication-slice';

dayjs.extend(customParseFormat);

interface OwnProps {
  field?: string;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  typeValue?: string;
  textChange: (value: string, field: string, error?: { error: boolean; message: string }) => void;
  validators?: FormValidators[];
  hasError?: boolean;
  fieldError?: string | boolean;
  label?: string;
  requiredMessage?: string;
  disableFuture?: boolean;
  minDate?: any;
  size?: any;
}

type TimeInputProps = OwnProps &
  Omit<
    DesktopTimePickerProps<any, any>,
    'onChange' | 'renderInput' | 'value' | 'onAccept' | 'onClose' | 'onError' | 'closeOnSelect'
  > & {
    value?: string | Dayjs | null;
    renderInput?: DesktopTimePickerProps<any, any>['renderInput'];
  };

const TIME_FORMATS = ['HH:mm', 'HH:mm:ss', 'h:mm A', 'hh:mm A'] as const;

export const parseTimeValue = (value: string | Dayjs | null | undefined): Dayjs | null => {
  if (value == null || value === '') return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    for (const fmt of TIME_FORMATS) {
      const parsed = dayjs(trimmed, fmt, true);
      if (parsed.isValid()) return parsed;
    }
  }
  return null;
};

const formatTimeValue = (value: Dayjs | null): string => {
  if (!value?.isValid()) return '';
  return value.format('HH:mm');
};

/** Normalizes form / picker values to HH:mm for API payloads. */
export const formatTimeForApi = (value: string | Dayjs | null | undefined): string => {
  const parsed = parseTimeValue(value);
  return parsed?.isValid() ? parsed.format('HH:mm') : '';
};

const invalidTimeError = () => ({ error: true, message: strings.time_format_error });

const TimeInput: FC<TimeInputProps> = ({
  hasError,
  validators,
  textChange,
  field,
  value,
  placeholder,
  fieldError,
  disabled,
  label,
  requiredMessage,
  size = 'small',
  renderInput: customRenderInput,
  PopperProps,
  componentsProps,
  ...rest
}) => {
  const locale = useSelector(defaultLanguage).value.split('_')[0];
  const initData = useSelector(parentOrgInitData);
  const theme = useTheme();
  const use24Hour = initData?.show24HourClock ?? false;
  const inputFormat = use24Hour ? 'HH:mm' : 'hh:mm A';

  const [pickerValue, setPickerValue] = useState<Dayjs | null>(() => parseTimeValue(value));
  const committedValueRef = useRef<Dayjs | null>(pickerValue);
  const [error, setError] = useState<{
    error: boolean;
    message: string;
  } | null>(null);

  useEffect(() => {
    const parsed = parseTimeValue(value);
    setPickerValue(parsed);
    committedValueRef.current = parsed;
  }, [value]);

  const commitValue = (nextValue: Dayjs | null) => {
    const formatted = formatTimeValue(nextValue);
    const inputError = validateInput(validators as FormValidators[], formatted || '');
    textChange(formatted, field as string, inputError || undefined);
    setError(inputError || null);
    const committed = nextValue?.isValid() ? nextValue : null;
    setPickerValue(committed);
    committedValueRef.current = committed;
  };

  const restoreCommittedValue = () => {
    setPickerValue(committedValueRef.current);
  };

  const handlePickerError = (reason: unknown, currentValue: Dayjs | null) => {
    if (reason) {
      const formatError = invalidTimeError();
      setError(formatError);
      textChange(formatTimeValue(currentValue) || '', field as string, formatError);
      return;
    }

    if (currentValue?.isValid()) {
      commitValue(currentValue);
      return;
    }

    setError(prev => (prev?.message === strings.time_format_error ? null : prev));
  };

  const validateTypedValue = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      setError(null);
      textChange('', field as string, validateInput(validators as FormValidators[], '') || undefined);
      committedValueRef.current = null;
      setPickerValue(null);
      return;
    }

    const parsed = parseTimeValue(trimmed);
    if (parsed?.isValid()) {
      commitValue(parsed);
      return;
    }

    const formatError = invalidTimeError();
    setError(formatError);
    textChange('', field as string, formatError);
  };

  const customMessage = () =>
    !error && hasError && fieldError ? requiredMessage || strings.requiredText : '';

  return (
    <Box className="timePicker" sx={{ width: '100%' }}>
      <LocalizationProvider
        adapterLocale={locale}
        dateAdapter={AdapterDayjs}
        localeText={{
          okButtonLabel: strings.okText,
          cancelButtonLabel: strings.cancelText,
        }}>
        <DesktopTimePicker
          {...rest}
          label={label || null}
          value={pickerValue}
          inputFormat={inputFormat}
          views={['hours', 'minutes']}
          showToolbar
          closeOnSelect={false}
          onChange={(newValue: Dayjs | null) => {
            setPickerValue(newValue);
          }}
          onError={handlePickerError}
          onAccept={(newValue: Dayjs | null) => commitValue(newValue)}
          onClose={restoreCommittedValue}
          disabled={disabled}
          className={`input-field ${fieldError}`}
          OpenPickerButtonProps={{ color: 'primary' }}
          minutesStep={15}
          shouldDisableTime={(time, type) => type === 'minutes' && time % 15 !== 0}
          ampm={!use24Hour}
          ampmInClock={!use24Hour}
          PopperProps={{
            ...PopperProps,
            sx: { zIndex: theme.zIndex.modal + 3, ...(PopperProps as any)?.sx },
          }}
          componentsProps={{
            ...componentsProps,
            actionBar: {
              actions: ['cancel', 'accept'],
              ...(componentsProps as any)?.actionBar,
            },
          }}
          renderInput={
            customRenderInput ??
            (params => (
              <TextField
                {...params}
                size={size}
                fullWidth
                placeholder={placeholder}
                helperText={error?.error ? error.message : customMessage()}
                error={Boolean((error && error.error) || (hasError && fieldError))}
                onBlur={event => {
                  params.onBlur?.(event);
                  validateTypedValue((event.target as HTMLInputElement).value);
                }}
              />
            ))
          }
        />
      </LocalizationProvider>
    </Box>
  );
};

export default TimeInput;
